/** GP50 straight-line cheat sheet metrics (shared by Cheat Sheet + Performance Drivers). */

export interface Gp50StraightLineMetricOption {
  label: string;
  value: string;
}

export const GP50_STRAIGHT_LINE_METRIC_OPTIONS: Gp50StraightLineMetricOption[] = [
  { label: "BSP", value: "bsp" },
  { label: "TWA", value: "twa" },
  { label: "VMG", value: "vmg" },
  { label: "HEEL_N", value: "heel_n" },
  { label: "PITCH", value: "pitch" },
  { label: "FOIL_RAKE", value: "foil_rake" },
  { label: "RH_LWD", value: "rh_lwd" },
  { label: "DB_PIERCING", value: "db_piercing_lwd" },
  { label: "RUD_RAKE", value: "rud_rake" },
  { label: "RUD_DIFF", value: "rud_diff" },
  { label: "DB_CANT", value: "db_cant" },
  { label: "DB_CANT_EFF", value: "db_cant_eff" },
  { label: "DB_CANT_STOW", value: "db_cant_stow" },
  { label: "WING_CA1", value: "wing_ca1" },
  { label: "WING_TWIST", value: "wing_twist" },
  { label: "WING_CLEW", value: "wing_clew" },
  { label: "JIB_SHT", value: "jib_sht" },
  { label: "JIB_SHT_%", value: "jib_sht_pct" },
  { label: "JIB_CUNNO", value: "jib_cunno" },
  { label: "JIB_LEAD", value: "jib_lead" },
  { label: "GAS", value: "gas_perc" },
];

/** Channel-mode straight-line table column order (config first, metrics, count last). */
export const GP50_STRAIGHT_LINE_TABLE_COLUMN_KEYS: readonly string[] = [
  "config",
  ...GP50_STRAIGHT_LINE_METRIC_OPTIONS.map((o) => o.value),
  "record_count",
];

/** API response keys (lowercase) -> events_aggregate column names (Performance Drivers envelope). */
export const GP50_STRAIGHT_LINE_API_KEY_TO_DB_COLUMN: Record<string, string> = {
  bsp: "Bsp_kph",
  twa: "Twa_n_deg",
  vmg: "Vmg_kph",
  heel_n: "Heel_n_deg",
  pitch: "Pitch_deg",
  foil_rake: "DB_rake_ang_lwd_deg",
  rh_lwd: "RH_lwd_mm",
  db_piercing_lwd: "DB_piercing_lwd_mm",
  rud_rake: "RUD_rake_ang_deg",
  rud_diff: "RUD_diff_ang_deg",
  db_cant: "DB_cant_lwd_deg",
  db_cant_eff: "DB_cant_eff_lwd_deg",
  db_cant_stow: "DB_cant_stow_tgt_deg",
  wing_ca1: "CA1_ang_n_deg",
  wing_twist: "WING_twist_n_deg",
  wing_clew: "WING_clew_pos_mm",
  jib_sht: "JIB_sheet_load_kgf",
  jib_sht_pct: "JIB_sheet_pct",
  jib_cunno: "JIB_cunno_load_kgf",
  jib_lead: "JIB_lead_ang_deg",
  gas_perc: "Gas_perc",
};

/** API response keys (lowercase) -> column header. */
export const GP50_STRAIGHT_LINE_API_HEADERS: Record<string, string> = {
  config: "CONFIG",
  bsp: "BSP [KPH]",
  twa: "TWA [DEG]",
  vmg: "VMG [KPH]",
  heel_n: "HEEL N [DEG]",
  pitch: "PITCH [DEG]",
  foil_rake: "FOIL RAKE [DEG]",
  rh_lwd: "RH LWD [MM]",
  db_piercing_lwd: "DB PIERCING [MM]",
  rud_rake: "RUD RAKE [DEG]",
  rud_diff: "RUD DIFF [DEG]",
  db_cant: "DB CANT [DEG]",
  db_cant_eff: "DB CANT EFF [DEG]",
  db_cant_stow: "DB CANT STOW [DEG]",
  wing_ca1: "WING CA1 [DEG]",
  wing_twist: "WING TWIST [DEG]",
  wing_clew: "WING CLEW [MM]",
  jib_sht: "JIB SHT [KGF]",
  jib_sht_pct: "JIB SHT [%]",
  jib_cunno: "JIB CUNNO [KGF]",
  jib_lead: "JIB LEAD [DEG]",
  gas_perc: "GAS [%]",
  record_count: "Count",
};

/** events_aggregate column names -> table header (Performance Drivers envelope). */
export const GP50_STRAIGHT_LINE_DB_COLUMN_LABELS: Record<string, string> = {
  Tws_kph: "TWS [KPH]",
  Twa_n_deg: "TWA [DEG]",
  Bsp_kph: "BSP [KPH]",
  Vmg_kph: "VMG [KPH]",
  Lwy_n_deg: "LWY N [DEG]",
  Heel_n_deg: "HEEL N [DEG]",
  Pitch_deg: "PITCH [DEG]",
  DB_rake_ang_lwd_deg: "FOIL RAKE [DEG]",
  RH_lwd_mm: "RH LWD [MM]",
  DB_piercing_lwd_mm: "DB PIERCING [MM]",
  RUD_rake_ang_deg: "RUD RAKE [DEG]",
  RUD_diff_ang_deg: "RUD DIFF [DEG]",
  DB_cant_lwd_deg: "DB CANT [DEG]",
  DB_cant_eff_lwd_deg: "DB CANT EFF [DEG]",
  DB_cant_stow_tgt_deg: "DB CANT STOW [DEG]",
  CA1_ang_n_deg: "WING CA1 [DEG]",
  WING_twist_n_deg: "WING TWIST [DEG]",
  WING_clew_pos_mm: "WING CLEW [MM]",
  JIB_sheet_load_kgf: "JIB SHT [KGF]",
  JIB_sheet_pct: "JIB SHT [%]",
  JIB_cunno_load_kgf: "JIB CUNNO [KGF]",
  JIB_lead_ang_deg: "JIB LEAD [DEG]",
  Gas_perc: "GAS [%]",
};
